import { FC } from 'react';
import clsx from 'clsx';
import type { Conversation } from '@theraply/api-client';
import { format, isToday, isYesterday, parseISO } from 'date-fns';

export interface ConversationListProps {
  conversations: Conversation[];
  activeConversationId?: number;
  onConversationClick: (conversation: Conversation) => void;
  className?: string;
}

export const ConversationList: FC<ConversationListProps> = ({
  conversations,
  activeConversationId,
  onConversationClick,
  className,
}) => {
  const formatTimestamp = (timestamp?: string) => {
    if (!timestamp) return '';
    const date = parseISO(timestamp);
    if (isToday(date)) {
      return format(date, 'h:mm a');
    } else if (isYesterday(date)) {
      return 'Yesterday';
    } else {
      return format(date, 'MMM d');
    }
  };

  if (conversations.length === 0) {
    return (
      <div className={clsx('flex flex-col items-center justify-center p-8 text-center', className)}>
        <svg
          className="w-16 h-16 text-gray-300 mb-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
          />
        </svg>
        <h3 className="text-lg font-medium text-gray-900 mb-1">No conversations yet</h3>
        <p className="text-sm text-gray-500">Start chatting to see your conversations here</p>
      </div>
    );
  }

  return (
    <div className={clsx('divide-y divide-gray-200', className)}>
      {conversations.map((conversation) => {
        const isActive = conversation.id === activeConversationId;
        const hasUnread = (conversation.unreadCount ?? 0) > 0;

        return (
          <button
            key={conversation.id}
            onClick={() => onConversationClick(conversation)}
            className={clsx(
              'w-full flex items-center gap-3 p-4 transition-colors hover:bg-gray-50',
              'focus:outline-none focus:bg-gray-50',
              isActive && 'bg-primary-50 hover:bg-primary-50 focus:bg-primary-50'
            )}
          >
            {/* Avatar */}
            <div className="relative flex-shrink-0">
              {conversation.otherParticipantAvatar ? (
                <img
                  src={conversation.otherParticipantAvatar}
                  alt={conversation.otherParticipantName || 'User'}
                  className="w-12 h-12 rounded-full object-cover"
                />
              ) : (
                <div className="w-12 h-12 rounded-full bg-gray-300 flex items-center justify-center text-gray-600 font-medium">
                  {conversation.otherParticipantName
                    ? conversation.otherParticipantName.charAt(0).toUpperCase()
                    : '?'}
                </div>
              )}

              {/* Online indicator */}
              {conversation.otherParticipantOnline && (
                <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 border-2 border-white rounded-full" />
              )}
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0 text-left">
              {/* Name and timestamp */}
              <div className="flex items-center justify-between mb-1">
                <h3
                  className={clsx(
                    'font-medium text-sm truncate',
                    hasUnread ? 'text-gray-900' : 'text-gray-700'
                  )}
                >
                  {conversation.otherParticipantName || 'Unknown User'}
                </h3>
                {conversation.lastMessageAt && (
                  <span className="text-xs text-gray-500 flex-shrink-0 ml-2">
                    {formatTimestamp(conversation.lastMessageAt)}
                  </span>
                )}
              </div>

              {/* Last message preview */}
              <div className="flex items-center justify-between">
                <p
                  className={clsx(
                    'text-sm truncate',
                    hasUnread ? 'text-gray-900 font-medium' : 'text-gray-500'
                  )}
                >
                  {conversation.lastMessagePreview || 'No messages yet'}
                </p>

                {/* Unread badge */}
                {hasUnread && (
                  <span className="flex-shrink-0 ml-2 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-bold text-white bg-primary-600 rounded-full">
                    {conversation.unreadCount}
                  </span>
                )}
              </div>
            </div>
          </button>
        );
      })}
    </div>
  );
};

ConversationList.displayName = 'ConversationList';
